import { useMemo } from "react";
import { CheckCircle2 } from "lucide-react";
import { calculateETA, formatPercentage, calculateMetricsFromTasks } from "@/lib/utils";
import type { DashboardState } from "@/types/dashboard";

interface ProgressBarProps {
  state: DashboardState;
}

const CALCULATING = "Berekenen…";

export function ProgressBar({ state }: ProgressBarProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (state.tasks && Object.keys(state.tasks).length > 0) {
      return calculateMetricsFromTasks(state.tasks, state.total_questions);
    }
    return state.metrics;
  }, [state.tasks, state.total_questions, state.metrics]);

  const { completed, total, percentage, eta, isCompleted } = useMemo(() => {
    const successful = metrics?.completed || 0;
    const failed = metrics?.failed || 0;
    const completed = successful + failed;
    const total = state.total_questions || 1;
    const percentage = Math.min(100, (completed / total) * 100);
    const running = metrics?.running || 0;
    const queued = metrics?.queued || 0;

    // Check if actually completed (no running/queued tasks)
    const isCompleted = running === 0 && queued === 0 && completed >= total;

    let eta = CALCULATING;
    if (!isCompleted && state.started_at && completed > 0) {
      const elapsedMs = Date.now() - new Date(state.started_at).getTime();
      const raw = calculateETA(completed, total, elapsedMs);
      eta = raw === "Calculating..." ? CALCULATING : raw;
    } else if (isCompleted) {
      eta = "Voltooid";
    }

    return { completed, total, percentage, eta, isCompleted };
  }, [metrics, state.total_questions, state.started_at]);

  const successPct = ((metrics?.completed || 0) / total) * 100;
  const failedPct = ((metrics?.failed || 0) / total) * 100;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-2xl font-semibold tabular-nums text-foreground">
            {completed} <span className="text-muted-foreground">/ {total}</span>
          </span>
          <span className="text-sm text-muted-foreground">{formatPercentage(percentage, 0)} verwerkt</span>
        </div>
        {isCompleted ? (
          <div className="flex items-center gap-1.5 text-sm font-medium text-success">
            <CheckCircle2 className="h-4 w-4" />
            Voltooid
          </div>
        ) : (
          <div className="text-sm">
            <span className="text-muted-foreground">Resterende tijd: </span>
            <span className="font-medium tabular-nums text-foreground">{eta}</span>
          </div>
        )}
      </div>

      <div
        className="relative h-3 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percentage)}
        aria-label="Voortgang van de evaluatie"
      >
        {isCompleted ? (
          <>
            <div className="absolute inset-y-0 left-0 bg-success transition-all duration-700 ease-out" style={{ width: `${successPct}%` }} />
            <div
              className="absolute inset-y-0 bg-destructive transition-all duration-700 ease-out"
              style={{ left: `${successPct}%`, width: `${failedPct}%` }}
            />
          </>
        ) : (
          <div className="h-full rounded-full bg-sl-yellow transition-all duration-700 ease-out" style={{ width: `${percentage}%` }} />
        )}
      </div>

      {metrics && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <Stat dot="bg-sl-yellow animate-pulse" label="Bezig" value={metrics.running} />
          <Stat dot="bg-sl-grey-300" label="In wachtrij" value={metrics.queued} />
          <Stat dot="bg-success" label="Voltooid" value={metrics.completed} />
          <Stat dot="bg-destructive" label="Mislukt" value={metrics.failed} valueClass="text-destructive" />
        </div>
      )}
    </div>
  );
}

function Stat({ dot, label, value, valueClass }: { dot: string; label: string; value: number; valueClass?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${valueClass ?? "text-foreground"}`}>{value}</span>
    </div>
  );
}
